import { useDebouncedSearchValue } from 'hooks/shared/useDebouncedSearchValue';
import { useMemo } from 'react';
import useQuery from 'hooks/react-query/useQuery';

import { AICacheKeys } from 'types/cacheKeys.types';
import { AvailableLanguages } from 'types/users.types';
import { includesPattern } from './utils';
import { keepPreviousData } from '@tanstack/react-query';

const flatSearchableCategories = (categories: any[], parent?: any): any[] =>
  categories.flatMap((category: any) => {
    if (category.isFinal || category.isTaxDeductible)
      return { ...category, parent: parent?.id || category.id };

    return category.list
      ? flatSearchableCategories(category.list, category)
      : [];
  });

const getFinalCategoriesDict = (categories: any[]) =>
  flatSearchableCategories(categories).reduce((acc, cate) => {
    acc[cate.id] = cate;
    return acc;
  }, {});

const mapBackendSearchResults = (
  results: any[],
  finalCategoriesDict: Record<string, any>,
  categoriesDict: Record<string, any>,
) => {
  const mappedResults = results
    ?.map(({ category_id, similarity }: any, index: number) =>
      finalCategoriesDict[category_id]
        ? { ...finalCategoriesDict[category_id], similarity, rank: index }
        : null,
    )
    .filter(Boolean)
    .reduce((acc, cat) => {
      if (!acc[cat.parent]) {
        acc[cat.parent] = {
          ...categoriesDict[cat.parent],
          list: [],
          similarity: 0,
        };
      }
      acc[cat.parent].list.push(cat);
      if (cat.similarity > acc[cat.parent].similarity) {
        acc[cat.parent].similarity = cat.similarity;
      }
      return acc;
    }, {});

  return Object.values(mappedResults).sort(
    (a: any, b: any) => b.similarity - a.similarity,
  );
};

const simpleSearch = (categories: any[], searchValue = '') => {
  const searchRecursively = (item: any): any[] => {
    const { title, displayName, description, keywords } = item;
    const isMatch = includesPattern({
      title,
      displayName,
      description,
      keywords,
      searchValue,
    });

    if (isMatch && item.isFinal) {
      return [{ ...item, title: item.displayName || item.title }];
    }

    if (item.list) {
      const results = item.list.flatMap(searchRecursively);
      return results.length > 0 ? results : isMatch ? [item] : [];
    }

    return [];
  };

  return categories
    .map((subCategory: any) => ({
      ...subCategory,
      list: subCategory.list.flatMap(searchRecursively),
    }))
    .filter((subCategory: any) => subCategory.list.length > 0);
};

type UseFilteredCategoriesProps = {
  backendSearchCategoryFn?: (searchValue: string) => Promise<{ data: any[] }>;
  searchValue: string;
  categoriesDict: Record<string, any>;
  lang: AvailableLanguages;
  currentLayer: any;
};

export const useFilteredCategories = ({
  backendSearchCategoryFn,
  searchValue,
  categoriesDict,
  lang,
  currentLayer,
}: UseFilteredCategoriesProps) => {
  const debouncedSearchValue = useDebouncedSearchValue(searchValue);

  const finalCategoriesDict = useMemo(
    () => getFinalCategoriesDict(categoriesDict?.['0']?.list),
    [categoriesDict?.['0']?.list],
  );

  const backendSearchEnabled = Boolean(
    backendSearchCategoryFn &&
      debouncedSearchValue?.length > 2 &&
      currentLayer.isAllCategories,
  );

  const { data: backendSearchResults, isError } = useQuery(
    [AICacheKeys.SearchCategories, debouncedSearchValue, lang],
    () => backendSearchCategoryFn?.(debouncedSearchValue),
    {
      enabled: backendSearchEnabled,
      placeholderData: keepPreviousData,
      select: (res) =>
        mapBackendSearchResults(
          res?.data || [],
          finalCategoriesDict,
          categoriesDict,
        )?.filter(Boolean),
    },
  );

  // if the search value is empty, return the categories
  if (!searchValue?.length) return currentLayer?.list ?? [];

  // if the search value is not empty, return the backend search results or the simple search results
  return backendSearchEnabled && !isError && backendSearchResults?.length
    ? backendSearchResults
    : simpleSearch(currentLayer?.list ?? [], searchValue);
};
